import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toAssetUrl } from "../api/api";
import { deleteCategory, fetchCategories, updateCategory } from "../api/categoriesApi";
import Loader from "./Loader";
import ConfirmModal from "./ConfirmModal";
import ToggleSwitch from "./ToggleSwitch";
import CategoryForm from "./CategoryForm";
import "../styles/CategoryList.css";

export default function CategoryList() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // { mode: "create" | "edit", category?: {...} } | null
  const [formState, setFormState] = useState(null);

  function load() {
    setLoading(true);
    setError("");
    return fetchCategories()
      .then((data) => setCategories(data))
      .catch((err) => {
        setError(err?.response?.data?.message || err.message || "Failed to load categories.");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
  }, []);

  async function handleToggleStatus(cat) {
    const next = !Boolean(Number(cat.status));
    setBusyId(cat.id);
    try {
      await updateCategory(cat.id, { name: cat.name, status: next, sort_order: cat.sort_order });
      setCategories((prev) =>
        prev.map((c) => (c.id === cat.id ? { ...c, status: next ? 1 : 0 } : c))
      );
    } catch (err) {
      alert(err?.response?.data?.message || err.message || "Could not update status.");
    } finally {
      setBusyId(null);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await deleteCategory(deleteTarget.id);
      setCategories((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      alert(err?.response?.data?.message || err.message || "Could not delete category.");
    } finally {
      setBusyId(null);
    }
  }

  function handleFormSaved() {
    setFormState(null);
    load();
  }

  return (
    <div className="categorylist">
      <div className="categorylist-toolbar">
        <div>
          <h2 className="categorylist-title">Categories</h2>
          <p className="categorylist-hint">
            Categories power the filter on the Posts page — create one here before assigning it to a post.
          </p>
        </div>
        <button
          type="button"
          className="postlist-create-btn"
          onClick={() => setFormState({ mode: "create" })}
        >
          + New Category
        </button>
      </div>

      {loading && <Loader label="Loading categories..." />}
      {!loading && error && <p className="postlist-error">{error}</p>}

      {!loading && !error && categories.length === 0 && (
        <p className="postlist-empty">No categories yet — create your first one.</p>
      )}

      {!loading && !error && categories.length > 0 && (
        <div className="categorylist-grid">
          {categories.map((cat) => {
            const isBusy = busyId === cat.id;
            const active = Boolean(Number(cat.status));
            return (
              <div className="categorycard" key={cat.id}>
                <div className="categorycard-image">
                  {cat.image ? (
                    <img src={toAssetUrl(cat.image)} alt={cat.name} />
                  ) : (
                    <div className="categorycard-img-placeholder" />
                  )}
                  <span
                    className={
                      "categorycard-status-badge" +
                      (active ? " categorycard-status-active" : " categorycard-status-inactive")
                    }
                  >
                    {active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="categorycard-body">
                  <div className="categorycard-heading-row">
                    <h3 className="categorycard-title">{cat.name}</h3>
                    <span className="categorycard-order">#{cat.sort_order ?? "—"}</span>
                  </div>

                  <ToggleSwitch
                    label={active ? "Active" : "Inactive"}
                    checked={active}
                    onChange={() => handleToggleStatus(cat)}
                    disabled={isBusy}
                  />

                  <div className="categorycard-actions">
                    <button
                      type="button"
                      className="postcard-btn postcard-btn-edit"
                      onClick={() => setFormState({ mode: "edit", category: cat })}
                    >
                      Edit
                    </button>
                    {/* <button
                      className="postcard-btn postcard-btn-delete"
                      onClick={() => setDeleteTarget(cat)}
                      disabled={isBusy}
                    >
                      Delete
                    </button> */}
                    <Link
                      className="postcard-btn postcard-btn-view"
                      to={`/posts?category=${cat.id}`}
                    >
                      View posts
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CategoryForm
        open={!!formState}
        mode={formState?.mode}
        category={formState?.category}
        onClose={() => setFormState(null)}
        onSaved={handleFormSaved}
      />

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete this category?"
        message={
          deleteTarget
            ? `"${deleteTarget.name}" will be permanently removed. Posts already assigned to it will keep the old category_id.`
            : ""
        }
        confirmLabel="Delete"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
